import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Ban, CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { type PublicProposalData, publicProposalApi } from '@/api/commercial/public-proposal';
import { StepNavigation } from '@/components/commercial/accounts/create/step-navigation';
import {
  canSubmitAcceptance,
  getAcceptanceDeclaration,
} from '@/components/commercial/proposals/acceptance/acceptance-declaration';
import { AcceptancePaymentStep } from '@/components/commercial/proposals/acceptance/acceptance-payment-step';
import { ContractStep } from '@/components/commercial/proposals/acceptance/contract/contract-step';
import { usePublicProposalContractStep } from '@/components/commercial/proposals/acceptance/contract/use-public-proposal-contract-step';
import { PublicProposalBanner } from '@/components/commercial/proposals/acceptance/public-proposal-banner';
import { PublicProposalCenteredLayout } from '@/components/commercial/proposals/acceptance/public-proposal-centered-layout';
import {
  buildPublicProposalWizardSteps,
  DEFAULT_PUBLIC_STEP,
  resolvePublicProposalStep,
} from '@/components/commercial/proposals/acceptance/public-proposal-wizard';
import { ProposalStatusBadge } from '@/components/commercial/proposals/create/proposal-status-badge';
import { ResumeContactAddressSection } from '@/components/commercial/proposals/create/resume/resume-contact-address-section';
import { ResumeItemsSummaryCard } from '@/components/commercial/proposals/create/resume/resume-items-summary-card';
import { ResumeNegotiationInfoCard } from '@/components/commercial/proposals/create/resume/resume-negotiation-info-card';
import { ResumePaymentConditionsCard } from '@/components/commercial/proposals/create/resume/resume-payment-conditions-card';
import { ResumeSellerSection } from '@/components/commercial/proposals/create/resume/resume-seller-section';
import {
  ResumeCard,
  ResumeCardHeader,
} from '@/components/commercial/proposals/shared/summary-components';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useAcceptProposal } from '@/hooks/use-accept-proposal';
import { usePublicProposal } from '@/hooks/use-public-proposal';
import { useRefuseProposal } from '@/hooks/use-refuse-proposal';
import { useSendOtp } from '@/hooks/use-send-otp';
import { useTrackEvent } from '@/hooks/use-track-event';
import { useVerifyOtp } from '@/hooks/use-verify-otp';
import { cn } from '@/lib/utils';
import { calculateCartSummaryCategories } from '@/utils/commercial/cart-summary-calculations';
import {
  adaptPublicAddress,
  adaptPublicBreakdown,
  adaptPublicContacts,
  adaptPublicPayment,
  adaptPublicPaymentSummary,
  adaptPublicProposalItems,
} from '@/utils/commercial/public-proposal-resume';

export function ProposalAcceptancePage() {
  const { token } = useParams<{ token: string }>();
  const [isVerified, setIsVerified] = useState(false);
  const [isRefused, setIsRefused] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const observation = useQuery({
    queryKey: ['public-payment', token],
    queryFn: () => publicProposalApi.findPayment(token!),
    enabled: Boolean(token) && !isVerified,
    retry: false,
  });

  if (!token) {
    return <LinkUnavailableState />;
  }

  if (['APPROVED', 'CANCELED'].includes(observation.data?.obligation.status ?? '')) {
    return (
      <PublicProposalCenteredLayout>
        <AcceptancePaymentStep token={token} />
      </PublicProposalCenteredLayout>
    );
  }

  if (!isVerified) {
    if (observation.isLoading)
      return <PublicProposalCenteredLayout>Carregando pagamento...</PublicProposalCenteredLayout>;
    return <OtpGate key={token} token={token} onVerified={() => setIsVerified(true)} />;
  }

  if (isRefused) {
    return <RefusalConfirmation />;
  }

  if (isAccepted) {
    return <AcceptanceConfirmation />;
  }

  return (
    <ProposalDetails
      token={token}
      onRefused={() => setIsRefused(true)}
      onAccepted={() => setIsAccepted(true)}
    />
  );
}

const OTP_SLOT_KEYS = ['otp-1', 'otp-2', 'otp-3', 'otp-4', 'otp-5', 'otp-6'] as const;
type OtpStep = 'sending' | 'verify' | 'error';

const OTP_CARD_CLASS =
  'rounded-2xl border-gray-200/80 shadow-[0_2px_4px_rgba(0,0,0,0.04),0_12px_32px_-12px_rgba(0,0,0,0.12)]';

function OtpGate({ token, onVerified }: { token: string; onVerified: () => void }) {
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [cooldown, setCooldown] = useState(0);
  const [otpStep, setOtpStep] = useState<OtpStep>('verify');
  const [destination, setDestination] = useState('');
  const [linkUnavailable, setLinkUnavailable] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hasAutoSentOtpRef = useRef(false);

  const sendOtp = useSendOtp();
  const verifyOtp = useVerifyOtp();
  const { mutate: trackLinkViewed } = useTrackEvent();

  function handleSendOtpError(err: unknown) {
    const message =
      (err as { response?: { data?: { message?: string } } } | null | undefined)?.response?.data
        ?.message ?? '';
    if (message === 'Proposal not found' || message === 'Esta proposta está expirada') {
      setLinkUnavailable(true);
      return;
    }
    const match = /^Aguarde\s+(\d+)s/.exec(message);
    if (match) {
      setCooldown(Number(match[1]));
      setOtpStep('verify');
      return;
    }
    setOtpStep('error');
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: auto-send must fire exactly once per token, guarded by ref
  useEffect(() => {
    if (!token || hasAutoSentOtpRef.current) return;
    // Defer until subscriptions are mounted. StrictMode cancels the first effect
    // before replaying it, so its mutation callbacks must not start yet.
    const timer = setTimeout(() => {
      hasAutoSentOtpRef.current = true;
      trackLinkViewed({ token, action: 'LINK_VIEWED' });
      requestAnimationFrame(() => focusInput(0));
      sendOtp.mutate(token, {
        onSuccess: (response) => {
          setDestination(response.destination);
          setCooldown(60);
        },
        onError: handleSendOtpError,
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [token]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const code = digits.join('');
  const hasVerifyError = verifyOtp.isError;

  function focusInput(index: number) {
    const target = Math.max(0, Math.min(index, 5));
    inputRefs.current[target]?.focus();
    requestAnimationFrame(() => {
      const el = inputRefs.current[target];
      if (el) {
        el.select();
      }
    });
  }

  function clearVerifyError() {
    if (verifyOtp.isError) verifyOtp.reset();
  }

  function setDigitAt(index: number, value: string) {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  function handleChange(index: number, raw: string) {
    clearVerifyError();
    const sanitized = raw.replace(/\D/g, '');
    if (!sanitized) {
      setDigitAt(index, '');
      return;
    }
    if (sanitized.length === 1) {
      setDigitAt(index, sanitized);
      if (index < 5) focusInput(index + 1);
      return;
    }
    const chars = sanitized.split('');
    let cursor = index;
    setDigits((prev) => {
      const next = [...prev];
      for (const c of chars) {
        if (cursor >= 6) break;
        next[cursor] = c;
        cursor++;
      }
      return next;
    });
    focusInput(Math.min(cursor, 5));
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
      return;
    }
    if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      focusInput(index + 1);
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    clearVerifyError();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    setDigits((prev) => {
      const next = [...prev];
      pasted.split('').forEach((c, i) => {
        next[i] = c;
      });
      return next;
    });
    focusInput(Math.min(pasted.length, 5));
  }

  function handleVerify() {
    if (!token || code.length !== 6 || verifyOtp.isPending) return;
    verifyOtp.mutate(
      { token, code },
      {
        onSuccess: (data) => {
          if (data.verified) onVerified();
        },
      },
    );
  }

  function handleResend() {
    if (!token || cooldown > 0 || sendOtp.isPending) return;
    clearVerifyError();
    setDigits(['', '', '', '', '', '']);
    focusInput(0);
    sendOtp.mutate(token, {
      onSuccess: (response) => {
        setDestination(response.destination);
        setOtpStep('verify');
        setCooldown(60);
      },
      onError: handleSendOtpError,
    });
  }

  function handleRetryInitialSend() {
    if (!token || sendOtp.isPending) return;
    setOtpStep('sending');
    sendOtp.mutate(token, {
      onSuccess: (response) => {
        setDestination(response.destination);
        setOtpStep('verify');
        setCooldown(60);
        requestAnimationFrame(() => focusInput(0));
      },
      onError: handleSendOtpError,
    });
  }

  if (linkUnavailable) {
    return (
      <LinkUnavailableState description="Abra o link do e-mail mais recente da proposta. Se necessário, solicite um novo envio à nossa equipe." />
    );
  }

  if (otpStep === 'sending') {
    return (
      <PublicProposalCenteredLayout>
        <Card className={OTP_CARD_CLASS}>
          <CardHeader className="space-y-4 px-8 text-center sm:px-10">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Enviando código...</h1>
            <p className="text-sm text-gray-500">
              Estamos enviando um código de verificação para o e-mail do contato.
            </p>
          </CardHeader>
        </Card>
      </PublicProposalCenteredLayout>
    );
  }

  if (otpStep === 'error') {
    return <OtpSendErrorState onRetry={handleRetryInitialSend} isRetrying={sendOtp.isPending} />;
  }

  return (
    <PublicProposalCenteredLayout>
      <Card className={OTP_CARD_CLASS}>
        <CardHeader className="space-y-5 px-6 pb-0 pt-8 text-center sm:px-10 sm:pt-10">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Insira o código de verificação
          </h1>
          <div className="space-y-1.5">
            <p className="text-sm text-gray-500">
              Enviamos um código de 6 dígitos para o seu e-mail:
            </p>
            {destination && <p className="text-sm font-semibold text-gray-700">{destination}</p>}
          </div>
        </CardHeader>

        <CardContent className="space-y-7 px-6 pb-8 pt-7 sm:px-10 sm:pb-10">
          <fieldset
            className="m-0 flex justify-center gap-3 border-0 p-0"
            aria-label="Código de verificação"
          >
            {OTP_SLOT_KEYS.map((key, index) => (
              <input
                key={key}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={6}
                value={digits[index]}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                aria-label={`Dígito ${index + 1}`}
                aria-invalid={hasVerifyError}
                className={cn(
                  'size-14 flex-none rounded-lg border bg-white text-center text-2xl font-semibold text-gray-900 transition-colors duration-200 sm:text-3xl',
                  'focus:outline-none focus:ring-4 focus:ring-blue-500/15',
                  hasVerifyError
                    ? 'border-red-400 focus:border-red-500'
                    : 'border-gray-200 focus:border-blue-500',
                )}
              />
            ))}
          </fieldset>

          {hasVerifyError && (
            <p className="text-center text-sm font-medium text-red-600" role="alert">
              Código incorreto
            </p>
          )}

          <button
            type="button"
            onClick={handleVerify}
            disabled={code.length !== 6 || verifyOtp.isPending}
            className="!mt-11 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-gray-900 text-sm font-semibold text-white transition-colors duration-200 hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {verifyOtp.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {verifyOtp.isPending ? 'Verificando...' : 'Confirmar'}
          </button>

          <div className="flex min-h-[1.25rem] items-center justify-center text-center text-xs text-gray-400">
            {sendOtp.isPending ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Enviando um código de 6 dígitos para seu e-mail...
              </span>
            ) : cooldown > 0 ? (
              <p>
                Você pode reenviar o código em{' '}
                <span className="font-semibold text-gray-700">{cooldown}s</span>
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={sendOtp.isPending}
                className="font-semibold text-gray-900 underline-offset-4 transition-colors hover:text-gray-700 hover:underline disabled:opacity-50"
              >
                {sendOtp.isPending ? 'Enviando...' : 'Reenviar código'}
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      <p className="mt-4 text-center text-xs text-gray-400">
        © {new Date().getFullYear()} BWS IoT. Todos os direitos reservados.
      </p>
    </PublicProposalCenteredLayout>
  );
}

function LinkUnavailableState({
  title = 'Este link não está mais disponível.',
  description = 'Em caso de dúvidas entre em contato com nossa equipe!',
  icon,
}: {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
}) {
  return (
    <PublicProposalCenteredLayout>
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
            {icon ?? <ShieldAlert className="h-6 w-6 text-gray-500" />}
          </div>
          <h1 className="text-xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500">{description}</p>
        </CardHeader>
      </Card>
    </PublicProposalCenteredLayout>
  );
}

function OtpSendErrorState({ onRetry, isRetrying }: { onRetry: () => void; isRetrying: boolean }) {
  return (
    <PublicProposalCenteredLayout>
      <Card className={OTP_CARD_CLASS}>
        <CardHeader className="space-y-4 px-8 text-center sm:px-10">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Não foi possível enviar o código.</h1>
          <p className="text-sm text-gray-500">
            Ocorreu um erro ao enviar o código de verificação. Tente novamente em instantes.
          </p>
        </CardHeader>
        <CardContent className="px-8 pb-8 sm:px-10 sm:pb-10">
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isRetrying && <Loader2 className="h-4 w-4 animate-spin" />}
            {isRetrying ? 'Enviando...' : 'Tentar novamente'}
          </button>
        </CardContent>
      </Card>
    </PublicProposalCenteredLayout>
  );
}

function ProposalDetails({
  token,
  onRefused,
  onAccepted,
}: {
  token: string;
  onRefused: () => void;
  onAccepted: () => void;
}) {
  const { data, isLoading, error } = usePublicProposal(token);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentStep = searchParams.get('step') || DEFAULT_PUBLIC_STEP;

  const contractStep = usePublicProposalContractStep(token);
  const contractSigned = contractStep.canAdvance;
  const hasAcceptancePaymentStep = Boolean(data);
  const hasImmediatePayment = data?.immediatePayment.required ?? false;

  const effectiveStep = resolvePublicProposalStep(currentStep, {
    includeAcceptancePayment: hasAcceptancePaymentStep,
    contractSigned,
  });

  useEffect(() => {
    if (effectiveStep !== currentStep) {
      setSearchParams({ step: effectiveStep }, { replace: true });
    }
  }, [effectiveStep, currentStep, setSearchParams]);

  const wizardSteps = useMemo(
    () =>
      buildPublicProposalWizardSteps(
        effectiveStep,
        hasAcceptancePaymentStep,
        contractSigned,
        hasImmediatePayment,
      ),
    [effectiveStep, hasAcceptancePaymentStep, contractSigned, hasImmediatePayment],
  );
  const currentStepIndex = wizardSteps.findIndex((step) => step.id === effectiveStep);
  const nextStep = wizardSteps[currentStepIndex + 1];
  const isFinalStep = !nextStep;
  const isDeliveryStep = effectiveStep === 'delivery';
  const isContractStep = effectiveStep === 'contract';
  const isContractBlocked = isContractStep && !contractSigned;
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [justification, setJustification] = useState('');
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [declarationError, setDeclarationError] = useState(false);
  const [paymentAccepted, setPaymentAccepted] = useState(false);
  const showPayment = paymentAccepted || data?.status === 'ACCEPTED_PENDING_PAYMENT';
  const trackedDetailsRef = useRef<string | null>(null);

  const refuseProposal = useRefuseProposal();
  const acceptProposal = useAcceptProposal();
  const { mutate: trackDetailsViewed } = useTrackEvent();
  const proposalNumber = data?.proposalNumber;

  useEffect(() => {
    if (!token || !proposalNumber) return;
    const trackingKey = `${token}:${proposalNumber}`;
    if (trackedDetailsRef.current === trackingKey) return;
    trackedDetailsRef.current = trackingKey;
    trackDetailsViewed({ token, action: 'DETAILS_VIEWED' });
  }, [token, proposalNumber, trackDetailsViewed]);

  const adaptedItems = useMemo(() => (data ? adaptPublicProposalItems(data.items) : []), [data]);
  const breakdown = useMemo(() => (data ? adaptPublicBreakdown(data.items) : null), [data]);
  const cartSummaryCategories = useMemo(() => {
    if (!data) {
      return {
        products: { grossInCents: 0, netInCents: 0, discountInCents: 0 },
        accessories: { grossInCents: 0, netInCents: 0, discountInCents: 0 },
        connectivity: { grossInCents: 0, netInCents: 0, discountInCents: 0 },
        monthlyServices: { grossInCents: 0, netInCents: 0, discountInCents: 0 },
        yearlyServices: { grossInCents: 0, netInCents: 0, discountInCents: 0 },
        fixedServices: { grossInCents: 0, netInCents: 0, discountInCents: 0 },
      };
    }
    return calculateCartSummaryCategories(adaptedItems, data.payment?.paymentType ?? 'CASH');
  }, [data, adaptedItems]);

  function handleConfirmRefuse() {
    if (justification.trim().length < 10) return;
    refuseProposal.mutate(
      { token, justification: justification.trim() },
      {
        onSuccess: (response) => {
          if (response.refused) {
            setRefuseOpen(false);
            onRefused();
          }
        },
      },
    );
  }

  function handleConfirmAccept() {
    if (!canSubmitAcceptance(declarationAccepted)) {
      setDeclarationError(true);
      return;
    }
    acceptProposal.mutate(
      { token, declarationAccepted: true },
      {
        onSuccess: (response) => {
          if (response.accepted) {
            setAcceptOpen(false);
            if (response.status === 'ACCEPTED_PENDING_PAYMENT') {
              setPaymentAccepted(true);
            } else {
              onAccepted();
            }
          }
        },
      },
    );
  }

  function handleNextStep() {
    if (nextStep) {
      setSearchParams({ step: nextStep.id }, { replace: true });
    }
  }

  function handlePrimaryAction() {
    if (nextStep) {
      handleNextStep();
      return;
    }
    setAcceptOpen(true);
  }

  function handleAcceptOpenChange(open: boolean) {
    if (acceptProposal.isPending) return;
    setAcceptOpen(open);
  }

  function handleOpenChange(open: boolean) {
    if (refuseProposal.isPending) return;
    setRefuseOpen(open);
    if (!open) setJustification('');
  }

  const refuseErrorMsg = (
    refuseProposal.error as { response?: { data?: { error?: string } } } | undefined
  )?.response?.data?.error;
  const acceptErrorMsg = (
    acceptProposal.error as { response?: { data?: { error?: string } } } | undefined
  )?.response?.data?.error;
  const justificationTooShort = justification.trim().length > 0 && justification.trim().length < 10;

  if (isLoading) {
    return (
      <PublicProposalCenteredLayout>
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Carregando proposta...</h1>
            <p className="text-sm text-gray-500">Por favor, aguarde enquanto buscamos os dados.</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </CardContent>
        </Card>
      </PublicProposalCenteredLayout>
    );
  }

  if (error || !data || !breakdown) {
    return <LinkUnavailableState />;
  }

  if (data.isExpired) {
    return (
      <LinkUnavailableState
        title="Esta proposta expirou."
        description="Em caso de dúvidas entre em contato com nossa equipe!"
        icon={<AlertTriangle className="h-6 w-6 text-amber-600" />}
      />
    );
  }

  const adaptedPayment = adaptPublicPayment(data.payment);
  const adaptedContacts = adaptPublicContacts(data.contactName, '');
  const adaptedAddress = adaptPublicAddress(data.deliveryAddress);
  const paymentSummary = adaptPublicPaymentSummary(data, breakdown, cartSummaryCategories);
  const totalDiscountInCents =
    cartSummaryCategories.products.discountInCents +
    cartSummaryCategories.accessories.discountInCents +
    cartSummaryCategories.connectivity.discountInCents +
    cartSummaryCategories.monthlyServices.discountInCents +
    cartSummaryCategories.yearlyServices.discountInCents +
    cartSummaryCategories.fixedServices.discountInCents;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <PublicProposalBanner />

      <main className="flex-1 px-4 py-8 sm:px-6 sm:py-10">
        <div className="mx-auto w-full max-w-6xl space-y-7">
          <PublicProposalHeader data={data} />

          <ResumeCard className="px-6 py-7">
            <StepNavigation steps={wizardSteps} />
          </ResumeCard>

          {showPayment ? (
            <AcceptancePaymentStep token={token} />
          ) : isDeliveryStep ? (
            <DeliveryAddressStep data={data} />
          ) : isContractStep ? (
            <ContractStep {...contractStep.stepProps} />
          ) : (
            <div className="space-y-5">
              <ResumeSellerSection sellerName={data.sellerName} />

              <ResumeContactAddressSection
                contacts={adaptedContacts}
                deliveryAddress={adaptedAddress}
                deliveryMode={data.deliveryMode}
                pickupLocationName={data.pickupLocationName}
              />

              <ResumeNegotiationInfoCard payment={adaptedPayment} paymentSummary={paymentSummary} />

              <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1.85fr)_minmax(340px,1fr)]">
                <ResumeItemsSummaryCard
                  items={adaptedItems}
                  paymentType={data.payment?.paymentType}
                />
                <ResumePaymentConditionsCard
                  payment={adaptedPayment}
                  breakdown={breakdown}
                  cartSummaryCategories={cartSummaryCategories}
                  paymentSummary={paymentSummary}
                  totalDiscountInCents={totalDiscountInCents}
                />
              </div>
            </div>
          )}

          {!showPayment && (
            <footer className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-6 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  if (isFinalStep || isContractStep) setRefuseOpen(true);
                }}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-6 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50"
              >
                <Ban className="h-4 w-4" />
                Recusar
              </button>
              {(!isContractStep || contractSigned) && (
                <button
                  type="button"
                  onClick={handlePrimaryAction}
                  disabled={isContractBlocked}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {isContractStep
                    ? 'Próxima etapa'
                    : nextStep
                      ? 'Validar e próximo'
                      : 'Aceitar proposta'}
                </button>
              )}
            </footer>
          )}

          <p className="text-center text-xs text-gray-400">
            © {new Date().getFullYear()} BWS IoT. Todos os direitos reservados.
          </p>
        </div>
      </main>

      <Dialog open={acceptOpen} onOpenChange={handleAcceptOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-blue-600" />
              Aceitar proposta
            </DialogTitle>
            <DialogDescription>
              Confirme o aceite da proposta <strong>#{data.proposalNumber}</strong>. Esta ação é
              irreversível e o vendedor responsável será notificado por e-mail.
            </DialogDescription>
          </DialogHeader>

          {acceptErrorMsg && <p className="text-sm text-red-600">{acceptErrorMsg}</p>}

          <div className="flex items-start gap-3">
            <Checkbox
              id="acceptance-declaration"
              checked={declarationAccepted}
              onCheckedChange={(checked) => {
                setDeclarationAccepted(checked === true);
                if (checked === true) setDeclarationError(false);
              }}
            />
            <label htmlFor="acceptance-declaration" className="text-sm text-gray-700">
              {getAcceptanceDeclaration(data.customerType)}
            </label>
          </div>
          {declarationError && (
            <p role="alert" className="text-sm text-red-600">
              Marque a declaração para aceitar a proposta.
            </p>
          )}

          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => handleAcceptOpenChange(false)}
              disabled={acceptProposal.isPending}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmAccept}
              disabled={acceptProposal.isPending}
              className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {acceptProposal.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirmar aceite
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={refuseOpen} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-600" />
              Recusar proposta
            </DialogTitle>
            <DialogDescription>
              Confirme a recusa da proposta <strong>#{data.proposalNumber}</strong>. Esta ação é
              irreversível e o vendedor responsável será notificado por e-mail.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label htmlFor="justification" className="text-sm font-medium text-gray-700">
              Justificativa <span className="text-red-600">*</span>
            </label>
            <Textarea
              id="justification"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Explique o motivo da recusa (mínimo 10 caracteres)..."
              rows={4}
              maxLength={500}
              disabled={refuseProposal.isPending}
            />
            <div className="flex items-center justify-between text-xs">
              <span className={justificationTooShort ? 'text-red-600' : 'text-gray-400'}>
                {justification.trim().length}/10 caracteres
              </span>
              <span className="text-gray-400">{justification.length}/500</span>
            </div>
            {refuseErrorMsg && <p className="text-sm text-red-600">{refuseErrorMsg}</p>}
          </div>

          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              disabled={refuseProposal.isPending}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmRefuse}
              disabled={justification.trim().length < 10 || refuseProposal.isPending}
              className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {refuseProposal.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirmar recusa
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DeliveryAddressStep({ data }: { data: PublicProposalData }) {
  const address = data.deliveryAddress;

  if (!address) {
    return (
      <ResumeCard className="px-5 py-4">
        <p className="text-sm text-gray-500">Endereço de entrega não informado.</p>
      </ResumeCard>
    );
  }

  const addressLine = [address.street, address.number, address.district].filter(Boolean).join(', ');

  return (
    <ResumeCard>
      <ResumeCardHeader
        title={
          data.deliveryMode === 'BWS'
            ? `Retirar na BWS — ${data.pickupLocationName ?? ''}`
            : 'Endereço de Entrega'
        }
      />

      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 p-5 sm:grid-cols-3 sm:gap-y-0">
        <div className="min-w-0">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Endereço
          </dt>
          <dd className="mt-1 break-words text-sm font-semibold text-gray-900">{addressLine}</dd>
          {address.complement && (
            <dd className="break-words text-xs text-gray-500">{address.complement}</dd>
          )}
        </div>

        <div className="min-w-0">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Cidade
          </dt>
          <dd className="mt-1 break-words text-sm font-semibold text-gray-900">
            {address.city}
            {address.state ? `/${address.state}` : ''}
          </dd>
        </div>

        <div className="min-w-0">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">CEP</dt>
          <dd className="mt-1 break-words text-sm font-semibold tabular-nums text-gray-900">
            {address.zipCode}
          </dd>
        </div>
      </dl>
    </ResumeCard>
  );
}

function PublicProposalHeader({ data }: { data: PublicProposalData }) {
  return (
    <ResumeCard className="px-5 py-5 sm:px-7 sm:py-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          Proposta #{data.proposalNumber}
        </h1>
        <ProposalStatusBadge status={data.status} />
      </div>
      <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
        <HeaderField label="Cliente" value={data.clientName} />
        <HeaderField label="Documento" value="—" />
        <HeaderField label="Emissão" value={data.sentDate ?? '—'} />
        <HeaderField label="Validade" value={data.validityDate ?? '—'} />
      </dl>
    </ResumeCard>
  );
}

function HeaderField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd
        className="mt-1 truncate text-sm font-semibold text-gray-900 sm:text-[15px]"
        title={value}
      >
        {value}
      </dd>
    </div>
  );
}

function AcceptanceConfirmation() {
  return (
    <PublicProposalCenteredLayout>
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Proposta aceita com sucesso.</h1>
          <p className="text-sm text-gray-500">
            O vendedor responsável foi notificado por e-mail e dará continuidade à operação.
          </p>
        </CardHeader>
      </Card>
      <p className="mt-4 text-center text-xs text-gray-400">
        © {new Date().getFullYear()} BWS IoT. Todos os direitos reservados.
      </p>
    </PublicProposalCenteredLayout>
  );
}

function RefusalConfirmation() {
  return (
    <PublicProposalCenteredLayout>
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <Ban className="h-6 w-6 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Proposta recusada com sucesso.</h1>
          <p className="text-sm text-gray-500">Agradecemos o retorno e ficamos à disposição.</p>
        </CardHeader>
      </Card>
      <p className="mt-4 text-center text-xs text-gray-400">
        © {new Date().getFullYear()} BWS IoT. Todos os direitos reservados.
      </p>
    </PublicProposalCenteredLayout>
  );
}
